import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function ForgotPasswordPage() {
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage(null);

        if (!email) {
            setError('Please enter your email address');
            setLoading(false);
            return;
        }

        const { error } = await resetPassword(email);

        if (error) {
            setError(error.message || 'Failed to send reset email');
        } else {
            setMessage('Check your email for a password reset link');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold">Trouble logging in?</CardTitle>
                    <CardDescription>
                        Enter your email and we'll send you a link to get back into your account.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        {message && (
                            <Alert className="bg-green-50 border-green-200 text-green-800">
                                <AlertDescription>{message}</AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email address</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="name@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading || !!message}
                            />
                        </div>

                        <Button type="submit" className="w-full" disabled={loading || !!message}>
                            {loading ? 'Sending link...' : 'Send reset link'}
                        </Button>

                        <div className="text-center mt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/signin')}
                                className="text-sm text-primary hover:underline font-medium"
                            >
                                Back to login
                            </button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
